<template>
  <grid-page-loading-error
    v-if="error !== null && !loading"
    @retry="init"
  />
  <div
    v-else-if="gridPageTileText === null"
    class="_spinner"
  >
    <m-spinner />
  </div>
  <m-content
    v-else
    :padding-top="gridPageTile.hideBorder ? -1 : 4"
    :padding-x="gridPageTile.hideBorder ? -1 : 9"
    :padding-bottom="gridPageTile.hideBorder ? -1 : 6"
    :class="['grid-page-tile-text', { '-hide-border': gridPageTile.hideBorder }]"
  >
    <m-editor
      :ref="(el) => contentElement = el"
      class="_m-editor"
      :initial-value="gridPageTileText.content"
      :allowed-content="allowedContent"
      light-placeholder
      :auto-focus="inlineEditing"
      :placeholder="$t('general.empty')"
      :disabled="props.readOnly"
      default-font-size="1.6rem"
      @input="updateContent"
      @blur="onBlur"
    />
  </m-content>
</template>

<script setup>
import GridPageLoadingError from '@/components/custom-grid/GridPageLoadingError.vue';
import MEditor from '@/components/editor/MEditor.vue';
import useAccess from '@/composables/access/access';
import useDebounce from 'shared/composables/debounce';
import useGridPage from '@/composables/grid-page/grid-page';
import useGridPageTextCardHeight from '@/composables/grid-page/grid-page-text-card-height';
import useInlineEditing from '@/composables/inline-editing';
import useSnackBar from '@/composables/snackbar';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { editorNodeType, featureFlag } from 'shared/constants.json';
import { logCatch } from '@/lib/logger/logger';

const props = defineProps({
  gridPageTile: { type: Object, required: true },
  readOnly: { type: Boolean, default: false },
});

const gridPageSvc = useGridPage();
const snackbar = useSnackBar();
const debounceSvc = useDebounce();

const gridPageTileText = computed(() => {
  const res = gridPageSvc.gridPageTileText.value.find((textTile) => textTile.tile.uid === props.gridPageTile.uid);
  if (res === undefined) {
    return null;
  }

  return res;
});

const inlineEditingSvc = useInlineEditing();
const inlineEditing = inlineEditingSvc.isInlineEditing(props.gridPageTile.uid);

const contentElement = ref(null);
const textCardHeightSvc = useGridPageTextCardHeight();
let resizeObserver = null;

const updateContent = (content) => {
  if (JSON.stringify(content) === JSON.stringify(gridPageTileText.value.content)) {
    return;
  }
  const updateFn = () => gridPageSvc.updateGridPageTileText({ uid: gridPageTileText.value.uid, content }).catch(logCatch(() => {
    snackbar.error();
  }));
  debounceSvc.debounce(updateFn, 500);

  nextTick(() => {
    adjustRowHeight();
  });
};

const adjustRowHeight = () => {
  if (!contentElement.value) {
    return;
  }
  textCardHeightSvc.adjustRowHeightForTextCard(props.gridPageTile, contentElement.value);
};

const onBlur = () => {
  if (inlineEditing.value === true) {
    inlineEditingSvc.reset();
  }
};

const setupResizeTracking = () => {
  if (!contentElement.value) {
    return;
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  resizeObserver = new ResizeObserver(() => {
    adjustRowHeight();
  });

  resizeObserver.observe(contentElement.value.$el);

  nextTick(() => {
    adjustRowHeight();
  });
};

watch(contentElement, () => {
  if (contentElement.value) {
    nextTick(() => {
      setupResizeTracking();
    });
  }
});

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

const error = ref(null);
const loading = ref(false);
const init = () => {
  loading.value = true;
  error.value = null;
  gridPageSvc.queryGridPageTileTextByTile(props.gridPageTile)
    .catch(logCatch((e) => {
      error.value = e;
    }))
    .finally(() => {
      loading.value = false;
    });
};

init();

const allowedContent = [
  editorNodeType.codeBlock,
  editorNodeType.blockquote,
  editorNodeType.bulletList,
  editorNodeType.orderedList,
  editorNodeType.todoList,
  editorNodeType.heading,
  editorNodeType.horizontalRule,
  editorNodeType.table,

  editorNodeType.goalMention,
];

const { accountHasFeature } = useAccess();
if (accountHasFeature([featureFlag.fileUpload])) {
  allowedContent.push(editorNodeType.image, editorNodeType.file);
}

</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
._spinner {
  height: var(--viewport-height-100);
  display: flex;
  justify-content: center;
  align-items: center;
}

.grid-page-tile-text {
  height: inherit;
  overflow: auto;

  ._m-editor {
    :deep(._editor) {
      height: 100%;

      [contenteditable="true"] {
        height: 100%;
      }
    }

    :deep(.ProseMirror) {
      // the document's own first block, not every element that happens to open its parent —
      // a todo item's checkbox opens its list item and is placed by a margin of its own
      > :first-child {
        margin-top: 0;
      }

      h1 {
        font-size: 3rem;
      }

      h2 {
        font-size: $font-size-8;
      }

      h3 {
        font-size: $font-size-7;
      }
    }
  }

}
</style>
